import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  ShieldCheck,
  Truck,
  PhoneCall,
  Sparkles,
  Zap,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { ASSET_IMAGES, PRODUCTS as DEFAULT_PRODUCTS } from '../../data/products';
import { STORE_CONFIG } from '../../config/storeConfig';
import { useProducts } from '../../context/ProductsContext';
import { useHero } from '../../context/HeroContext';
import { Product, HeroSlide } from '../../types/store';

interface HeroBannerProps {
  onShopAll: () => void;
  onExploreBundles: () => void;
  onSelectProduct?: (product: Product) => void;
}

const FALLBACK_HERO_BANNER = '/assets/images/malrich_hero_main_banner_1786910615726.webp';

function resolveSlideUrl(url?: string): string {
  if (!url || typeof url !== 'string') return ASSET_IMAGES.heroBanner || FALLBACK_HERO_BANNER;
  const trimmed = url.trim();
  if (
    trimmed.length < 5 ||
    trimmed === 'undefined' ||
    trimmed === 'null' ||
    trimmed.startsWith('blob:')
  ) {
    return ASSET_IMAGES.heroBanner || FALLBACK_HERO_BANNER;
  }
  return trimmed;
}

export const HeroBanner: React.FC<HeroBannerProps> = ({
  onShopAll,
  onExploreBundles,
  onSelectProduct,
}) => {
  const { products } = useProducts();
  const { activeHeroSlides } = useHero();
  const catalog = products && products.length > 0 ? products : DEFAULT_PRODUCTS;

  // Fallback default 16:9 slide if no custom slides in admin
  const defaultSlides: HeroSlide[] = [
    {
      id: 'default-hero-16-9',
      title: '',
      desktopImageUrl: ASSET_IMAGES.heroBanner || FALLBACK_HERO_BANNER,
      mobileImageUrl: ASSET_IMAGES.heroBanner || FALLBACK_HERO_BANNER,
      buttonLink: 'shop-all',
      active: true,
      order: 0,
    },
  ];

  const validActiveSlides = (activeHeroSlides || []).filter((s) => s && s.active !== false);
  const slidesToDisplay = validActiveSlides.length > 0 ? validActiveSlides : defaultSlides;

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (slidesToDisplay.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % slidesToDisplay.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slidesToDisplay.length, isPaused]);

  // Handle slide click navigation
  const handleSlideClick = (slide: HeroSlide) => {
    const link = slide.buttonLink || 'shop-all';
    if (link === 'bundles') {
      onExploreBundles();
    } else if (link.startsWith('product:')) {
      const prodId = link.replace('product:', '');
      const matched = catalog.find((p) => p.id === prodId);
      if (matched && onSelectProduct) {
        onSelectProduct(matched);
      } else {
        onShopAll();
      }
    } else {
      onShopAll();
    }
  };

  return (
    <section
      id="hero-banner-section"
      className="relative w-full bg-[#080B1E] text-white pt-0 pb-0 border-b border-blue-950/60"
    >
      {/* Primary Semantic H1 for SEO & Search Engine Indexing */}
      <h1 className="sr-only">Health Store – Weight Gain & Nutrition Products in Pakistan</h1>

      {/* ================= 16:9 PURE FULL-WIDTH IMAGE HERO SLIDER ================= */}
      <div
        className="relative w-full overflow-hidden bg-slate-950 group select-none"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Strict 16:9 Aspect Ratio Edge-to-Edge Container */}
        <div className="relative w-full aspect-[16/9] overflow-hidden bg-slate-950">
          {slidesToDisplay.map((slide, idx) => {
            const isActive = idx === currentSlideIndex;
            const imageUrl = resolveSlideUrl(slide.desktopImageUrl || slide.mobileImageUrl);
            const hasDistinctMobile =
              slide.mobileImageUrl &&
              slide.mobileImageUrl !== slide.desktopImageUrl &&
              !slide.mobileImageUrl.startsWith('blob:') &&
              slide.mobileImageUrl.length > 5;
            const mobileUrl = hasDistinctMobile ? resolveSlideUrl(slide.mobileImageUrl) : imageUrl;

            return (
              <div
                key={slide.id || idx}
                onClick={() => handleSlideClick(slide)}
                className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out cursor-pointer ${
                  isActive ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'
                }`}
              >
                {/* 100% Full-Screen 16:9 Banner Picture with No Side Borders */}
                <picture className="w-full h-full block">
                  {hasDistinctMobile && (
                    <source media="(max-width: 640px)" srcSet={mobileUrl} />
                  )}
                  <img
                    src={imageUrl}
                    alt={slide.title || 'MalRich Health Store Banner'}
                    className="w-full h-full object-cover object-center block"
                    referrerPolicy="no-referrer"
                    loading={idx === 0 ? 'eager' : 'lazy'}
                    decoding={idx === 0 ? 'sync' : 'async'}
                    fetchPriority={idx === 0 ? 'high' : 'auto'}
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (!target.src.includes('malrich_hero_main_banner')) {
                        target.src = FALLBACK_HERO_BANNER;
                      }
                    }}
                  />
                </picture>
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows (Shown when > 1 slide) */}
        {slidesToDisplay.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentSlideIndex((prev) =>
                  prev === 0 ? slidesToDisplay.length - 1 : prev - 1
                );
              }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-black/40 hover:bg-black/80 text-white backdrop-blur-xs flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 cursor-pointer border border-white/20 shadow-lg active:scale-95"
              aria-label="Previous Slide"
            >
              <ChevronLeft className="w-5 h-5 sm:w-7 sm:h-7" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentSlideIndex((prev) => (prev + 1) % slidesToDisplay.length);
              }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-black/40 hover:bg-black/80 text-white backdrop-blur-xs flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 cursor-pointer border border-white/20 shadow-lg active:scale-95"
              aria-label="Next Slide"
            >
              <ChevronRight className="w-5 h-5 sm:w-7 sm:h-7" />
            </button>

            {/* Indicator Dots */}
            <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 sm:gap-2 bg-black/50 px-3 py-1.5 rounded-full backdrop-blur-xs border border-white/10">
              {slidesToDisplay.map((_, dotIdx) => (
                <button
                  key={`dot-${dotIdx}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentSlideIndex(dotIdx);
                  }}
                  className={`h-2 sm:h-2.5 rounded-full transition-all cursor-pointer ${
                    dotIdx === currentSlideIndex
                      ? 'w-6 sm:w-8 bg-amber-400'
                      : 'w-2 sm:w-2.5 bg-white/50 hover:bg-white/90'
                  }`}
                  aria-label={`Go to slide ${dotIdx + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Quick Action Navigation Buttons Bar */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 my-3 sm:my-5">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
          
          {/* 1. Shop All Products Button */}
          <button
            id="hero-shop-all-btn"
            onClick={onShopAll}
            className="w-full sm:w-auto sm:flex-1 h-11 sm:h-12 bg-[#E11D48] hover:bg-[#BE123C] text-white px-5 sm:px-6 rounded-full font-extrabold text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-rose-900/30 transition-all duration-200 hover:scale-102 active:scale-95 border border-rose-400/40 whitespace-nowrap cursor-pointer"
          >
            <Zap className="w-4 h-4 text-amber-300" />
            <span>SHOP ALL PRODUCTS</span>
            <ArrowRight className="w-4 h-4" />
          </button>

          {/* 2. Bundle Deals Button */}
          <button
            id="hero-bundles-btn"
            onClick={onExploreBundles}
            className="w-full sm:w-auto sm:flex-1 h-11 sm:h-12 bg-[#191975] hover:bg-[#12125c] text-white px-5 sm:px-6 rounded-full font-bold text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all duration-200 hover:scale-102 active:scale-95 border border-blue-400/30 shadow-md whitespace-nowrap cursor-pointer"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span>BUNDLE DEALS (SAVE 25%)</span>
          </button>

          {/* 3. WhatsApp Direct Order Button */}
          <a
            href={STORE_CONFIG.getWhatsAppUrl('Salam! Mujhe MalRich Weight Gain Product order karna hai.')}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto sm:flex-1 h-11 sm:h-12 bg-emerald-600 hover:bg-emerald-700 text-white px-5 sm:px-6 rounded-full font-bold text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-200 hover:scale-102 active:scale-95 shadow-md border border-emerald-400/30 whitespace-nowrap cursor-pointer"
          >
            <PhoneCall className="w-4 h-4" />
            <span>WHATSAPP ORDER</span>
          </a>
        </div>
      </div>

      {/* Trust Highlights Strip below Hero Banner */}
      <div className="w-full bg-[#070B1F] border-t border-blue-900/60 py-2.5 sm:py-3 px-3 sm:px-4 text-[11px] sm:text-xs font-semibold text-slate-200">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-around gap-2 sm:gap-4 text-center">
          <div className="flex items-center gap-1.5">
            <Truck className="w-4 h-4 text-amber-400 shrink-0" />
            <span>Free Shipping Across Pakistan Above Rs. 2,000</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>100% Genuine & Halal Certified Nutrition</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-amber-300 shrink-0" />
            <span>Cash on Delivery (Pay at Your Doorstep)</span>
          </div>
        </div>
      </div>
    </section>
  );
};
